import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'
import {defaultProfileImage} from '../../../../utils/constants'
import {PublicExpert} from '../../../../services/expertsService'

// PR-I-82: componente propio para Experts. Antes reusabamos MemberSearchList con un adapter
// (company_name -> name, logo -> image); el adapter escondia que Member y Expert son entidades
// distintas y cualquier cambio para uno arriesgaba romper al otro. Este componente consume
// PublicExpert directo, sin renombres, para que el contrato sea explicito.
//
// Estilos y layout se copiaron del carrusel de members y viven bajo el namespace
// .experts-page__* en _nb-experts-search.scss — mismo look que hoy pero independiente.

// Mismo umbral que el carrusel de members (bootstrap "lg"): abajo de esto, solo swipe.
const ARROWS_MIN_WIDTH_PX = 992
// Ancho card (290) + gap (20).
const SCROLL_STEP_PX = 310

interface ExpertCardListProps {
  data: PublicExpert[]
  loading: boolean
  onItemClick: (id: number) => void
}

const ExpertCardList: FC<ExpertCardListProps> = ({data = [], loading, onItemClick}) => {
  const listRef = useRef<HTMLDivElement>(null)
  const [arrowsEnabled, setArrowsEnabled] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= ARROWS_MIN_WIDTH_PX : true,
  )
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  const updateEdges = useCallback(() => {
    const el = listRef.current
    if (!el) return
    const {scrollLeft, scrollWidth, clientWidth} = el
    // Tolerancia 2px por redondeo de scrollLeft con zoom != 100%.
    setCanPrev(scrollLeft > 2)
    setCanNext(scrollLeft + clientWidth < scrollWidth - 2)
  }, [])

  useEffect(() => {
    const onResize = () => {
      setArrowsEnabled(window.innerWidth >= ARROWS_MIN_WIDTH_PX)
      updateEdges()
    }
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [updateEdges])

  useEffect(() => {
    updateEdges()
  }, [data, updateEdges])

  const scrollBy = (offset: number) => {
    const el = listRef.current
    if (!el) return
    el.scrollBy({left: offset, behavior: 'smooth'})
  }

  if (loading) return <div className='experts-page__no-results'>Loading...</div>
  if (!data?.length) return <div className='experts-page__no-results'>No results found</div>

  return (
    <div className='experts-page__carousel'>
      {arrowsEnabled && canPrev && (
        <button
          type='button'
          aria-label='Previous experts'
          className='experts-page__arrow experts-page__arrow--prev'
          onClick={() => scrollBy(-SCROLL_STEP_PX)}
        >
          <svg width='10' height='16' viewBox='0 0 10 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M8.5 1L1.5 8L8.5 15' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </button>
      )}
      {arrowsEnabled && canNext && (
        <button
          type='button'
          aria-label='Next experts'
          className='experts-page__arrow experts-page__arrow--next'
          onClick={() => scrollBy(SCROLL_STEP_PX)}
        >
          <svg width='10' height='16' viewBox='0 0 10 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M1.5 1L8.5 8L1.5 15' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </button>
      )}
      <div
        className='experts-page__list'
        ref={listRef}
        onScroll={updateEdges}
      >
        {data.map((expert) => {
          // PublicExpert es el shape que devuelve el backend. `company_name` es el nombre del
          // expert (para un Partner tipo Expert, la razon social coincide con el nombre de la
          // persona), `expertise_count` alimenta el badge "N services", `logo` es la foto.
          const badge =
            expert.expertise_count && expert.expertise_count > 0
              ? `${expert.expertise_count} service${expert.expertise_count === 1 ? '' : 's'}`
              : ''
          const image = expert.logo ?? ''
          return (
            <div
              key={expert.id}
              className='experts-page__card'
              onClick={() => onItemClick(expert.id)}
            >
              <img
                src={toAbsoluteUrl(image)}
                alt={expert.company_name}
                className='experts-page__card-image'
                onError={(e) => {
                  e.currentTarget.src = defaultProfileImage
                }}
              />

              {/* Overlay default: badge + nombre. Visible siempre — es la unica pista de identidad
                  del experto cuando el mouse no esta sobre la card. */}
              <div className='experts-page__card-overlay-default'>
                {badge && (
                  <div className='experts-page__card-badge'>
                    <svg width='14' height='1' viewBox='0 0 14 1' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M0 0.25H13.5' stroke='white' strokeWidth='0.5' />
                    </svg>
                    {badge}
                    <svg width='14' height='1' viewBox='0 0 14 1' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M0 0.25H13.5' stroke='white' strokeWidth='0.5' />
                    </svg>
                  </div>
                )}
                <div className='experts-page__card-name'>{expert.company_name}</div>
              </div>

              {/* Overlay hover: repite badge + nombre y agrega direccion + Learn More. Mismo patron
                  que la card de member pero sin categorias — Partner Expert no las expone. */}
              <div className='experts-page__card-overlay-hover'>
                <div className='experts-page__card-header'>
                  {badge && (
                    <div className='experts-page__card-badge'>
                      <svg width='14' height='1' viewBox='0 0 14 1' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M0 0.25H13.5' stroke='white' strokeWidth='0.5' />
                      </svg>
                      {badge}
                      <svg width='14' height='1' viewBox='0 0 14 1' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M0 0.25H13.5' stroke='white' strokeWidth='0.5' />
                      </svg>
                    </div>
                  )}
                  <div className='experts-page__card-name'>{expert.company_name}</div>
                </div>
                {expert.address && (
                  <div className='experts-page__card-location'>
                    <div className='experts-page__card-location-icon'>
                      <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M7.00018 7.83334C8.00532 7.83334 8.82018 7.01854 8.82018 6.01334C8.82018 5.0082 8.00532 4.19336 7.00018 4.19336C5.99503 4.19336 5.18018 5.0082 5.18018 6.01334C5.18018 7.01854 5.99503 7.83334 7.00018 7.83334Z' stroke='white' />
                        <path d='M2.11182 4.95185C3.26099 -0.0998163 10.7451 -0.0939823 11.8885 4.95768C12.5593 7.92102 10.716 10.4293 9.10015 11.981C7.92765 13.1127 6.07265 13.1127 4.89432 11.981C3.28432 10.4293 1.44099 7.91518 2.11182 4.95185Z' stroke='white' />
                      </svg>
                    </div>
                    <div className='experts-page__card-location-text'>{expert.address}</div>
                  </div>
                )}
                <div className='experts-page__card-learn-more'>
                  <a
                    className='experts-page__card-learn-more-text'
                    onClick={(e) => {
                      // El click del <a> ya burbujea al card wrapper, no hace falta hacer nada
                      // adicional — el `onItemClick` del wrapper navega al detalle.
                      e.stopPropagation()
                      onItemClick(expert.id)
                    }}
                  >
                    Learn More
                  </a>
                  <svg width='14' height='4' viewBox='0 0 14 4' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M8.5 0.378906L12 3.38306H0' stroke='white' />
                  </svg>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export {ExpertCardList}
